/// Sapio rating logic needs to be shared with frontend,
/// because, to avoid duplication, sapio ratings are not
/// stored in the database, so frontend receives
/// raw insurers ratings.
import { InsuranceCompany } from '../enums/insurance-company.enum.js';
/**
 * Filters out ratings that are not valid for the given date, i.e.
 * that either :
 * - are outdated by a more recent rating at the given date
 * - have a start date after the given date
 * - have an end date before the given date
 *
 * It should thus return maximum one rating per insurance company.
 */
export const takeValidRatings = ({ ratings, date, }) => {
    if (!ratings.length)
        return [];
    const validByDate = ratings.filter((rating) => {
        if (rating.startDate > date)
            return false;
        if (rating.endDate !== null && rating.endDate < date)
            return false;
        return true;
    });
    const byCompany = new Map();
    validByDate.forEach((rating) => {
        const existing = byCompany.get(rating.insuranceCompany);
        if (!existing || rating.startDate > existing.startDate)
            byCompany.set(rating.insuranceCompany, rating);
    });
    return Array.from(byCompany.values());
};
export const sapioRating = (input) => {
    const validRatings = takeValidRatings(input);
    if (!validRatings.length)
        return null;
    const transformedRatings = validRatings
        .map((r) => ratingTransformers[r.insuranceCompany](r.value))
        .filter((v) => v !== null);
    if (!transformedRatings.length)
        return null;
    return (transformedRatings.reduce((acc, v) => acc + v, 0) /
        transformedRatings.length);
};
/** Allianz rating is the same as sapio rating, except 'NA' which is discarded */
const transformAllianzToSapioRating = (value) => {
    if (value === 'NA')
        return null;
    const valueNum = Number(value);
    if (Number.isNaN(valueNum))
        throw new Error(`Invalid Allianz grade : ${value}`);
    if (valueNum < 1 || valueNum > 10)
        throw new Error(`Invalid Allianz grade range : ${value}`);
    return valueNum;
};
/** Atradius rating is between 1 and 100, except 'N/A' which is discarded */
const transformAtradiusToSapioRating = (value) => {
    if (value === 'N/A')
        return null;
    const valueNum = Number(value);
    if (Number.isNaN(valueNum))
        throw new Error(`Invalid Atradius grade: ${value}`);
    if (valueNum < 1 || valueNum > 100)
        throw new Error(`Invalid Atradius grade range: ${value}`);
    return valueNum / 10;
};
/** Coface rating is between 1 and 10, but reversed compared to sapio rating */
const transformCofaceToSapioRating = (value) => {
    const valueNum = Number(value);
    if (Number.isNaN(valueNum))
        throw new Error(`Invalid Coface grade: ${value}`);
    if (valueNum < 0 || valueNum > 10)
        throw new Error(`Invalid Coface grade range: ${value}`);
    return 10 - (valueNum * 9) / 10;
};
const ratingTransformers = {
    [InsuranceCompany.Allianz]: transformAllianzToSapioRating,
    [InsuranceCompany.Atradius]: transformAtradiusToSapioRating,
    [InsuranceCompany.Coface]: transformCofaceToSapioRating,
};
export const ratingToSapio = (insuranceCompany, value) => {
    const transformer = ratingTransformers[insuranceCompany];
    if (!transformer)
        return null;
    try {
        return transformer(value);
    }
    catch {
        return null;
    }
};
